/** @jsx jsx */
import { css, jsx, SerializedStyles } from '@emotion/core'
import { Theme, useTheme } from 'bold-ui'
import { MChatRiscoEnum } from 'graphql/types.generated'
import { useMemo } from 'react'

import { MCHAT_RESULTADOS } from '../model-mchat'

interface MChatEscalaLineProps {
  line: MChatRiscoEnum
  risco: MChatRiscoEnum
  pontuacao: number
}

export function MChatEscalaLine(props: MChatEscalaLineProps) {
  const { line, risco, pontuacao } = props

  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme, pontuacao, risco, line), [risco, line, pontuacao, theme])
  const showIndicador = risco === line

  return (
    <div css={styles.bar}>
      <div css={styles.progress}>{showIndicador && <div css={styles.indicator} />}</div>
    </div>
  )
}

const PROGRESS_LINE: Record<MChatRiscoEnum, { min: number; max: number; span: number }> = {
  BAIXO: {
    min: 1,
    max: 2,
    span: 2,
  },
  MEDIO: {
    min: 3,
    max: 7,
    span: 5,
  },
  ELEVADO: {
    min: 8,
    max: 20,
    span: 13,
  },
}

const createStyles = (theme: Theme, pontuacao: number, risco: MChatRiscoEnum, line: MChatRiscoEnum) => {
  const { span } = PROGRESS_LINE[line]
  const progressSize = getProgressSize(line, pontuacao)
  const indicatorPosition = getIndicatorPosition(pontuacao)

  return {
    bar: css`
      height: 0.25rem;
      background-color: ${theme.pallete.gray.c80};
      grid-column: span ${span};
      display: grid;
      grid-template-columns: repeat(${span}, 1fr);
      position: ${pontuacao === 0 ? 'relative' : 'static'};
    `,
    progress: css`
      height: 0.25rem;
      background-color: ${MCHAT_RESULTADOS[risco].lineColor};
      grid-column: ${progressSize === 0 ? 'none' : `span ${progressSize}`};
      position: ${pontuacao === 0 ? 'static' : 'relative'};
    `,
    indicator: css`
      width: 1.5rem;
      height: 1.5rem;
      background: ${theme.pallete.surface.main};
      position: absolute;
      border-radius: 50%;
      top: -0.65rem;
      box-shadow: inset 0 0 0 0.5rem ${MCHAT_RESULTADOS[risco].textColor};
      ${indicatorPosition}
    `,
  }
}

function getProgressSize(risco: MChatRiscoEnum, pontuacao: number): number {
  const { min, max, span } = PROGRESS_LINE[risco]
  if (pontuacao < min || pontuacao === 0) return 0
  if (pontuacao >= min && pontuacao < max) return pontuacao - min + 1
  if (pontuacao >= max) return span
}

function getIndicatorPosition(pontuacao: number): SerializedStyles {
  if (pontuacao === 0) {
    return css`
      left: 0;
    `
  }
  if (pontuacao === 20) {
    return css`
      right: 0;
    `
  }
  return css`
    right: -0.75rem;
  `
}
